import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { useEffect } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import ClosIcon from "src/assets/svg-icons/close.svg";

const MULTI_SELF_PARKING_LOCATION_IDS: Record<string, string> = {
  development: "ya2qwvBqMgTUCRQnA3jv",
  prod: "cepeOZWeB0Xiojvw0fCq",
};

export const schema = yup.object().shape({
  type: yup
    .string()
    .required("required")
    .oneOf([
      "valet",
      "selfParking",
      "golfCar",
      "partialCarWash",
      "fullCarWash",
    ] as const),
  name: yup.string().required("Required"),
  payment: yup
    .string()
    .required("required")
    .oneOf(["entry", "exit", "flexible"] as const),
  pricingType: yup
    .string()
    .required("required")
    .oneOf(["free", "fixed", "custom", "hourly", "daily"] as const),
  price: yup.number().when("pricingType", {
    is: (value: string) => value !== "free",
    then: (value) =>
      value
        .typeError("Required")
        .test(
          "price-validation",
          "Must be a positive number",
          (value, context) => {
            const pricingType = context.parent.pricingType;

            if (pricingType === "custom") {
              return value !== undefined && value >= 0; // Must be >= 0
            }

            if (["fixed", "hourly"].includes(pricingType)) {
              return value !== undefined && value > 0; // Must be > 0
            }

            return true; // Optional for other cases
          },
        )
        .required("Required"),
    otherwise: (value) => value.nullable().notRequired(),
  }),

  gracePeriod: yup.number().when(["pricingType", "payment"], {
    is: (pricingType: string, payment: string) =>
      ["fixed", "custom", "hourly"].includes(pricingType) && payment === "exit",
    then: (value) =>
      value
        .typeError("Required")
        .min(0, "Must be a positive number or zero")
        .required("Required"),
    otherwise: (value) => value.nullable().notRequired(),
  }),

  startingHours: yup.number().when("pricingType", {
    is: "custom",
    then: (value) =>
      value
        .typeError("Required")
        .min(0, "Must be a positive number or zero")
        .required("Required"),
    otherwise: (value) => value.nullable().notRequired(),
  }),
  priceOfExceedingHours: yup.number().when("pricingType", {
    is: "custom",
    then: (value) =>
      value
        .typeError("Required")
        .min(0, "Must be a positive number or zero")
        .required("Required"),
    otherwise: (value) => value.nullable().notRequired(),
  }),
  pricingModal: yup
    .string()
    .required("required")
    .oneOf(["customByHours"] as const),
  exceedingPricingType: yup
    .string()
    .required("required")
    .oneOf(["hourly", "fixed", "twelveHour"] as const),
  ticketViolationPrice: yup
    .number()
    .typeError("Required")
    .min(0, "Must be a positive number or zero"),
});

export interface ServiceFormInputs extends yup.InferType<typeof schema> {}

const StyledRadioGroup = styled(RadioGroup)({
  "& .MuiFormControlLabel-label": {
    fontSize: "12px", // Set your desired font size here
  },
});

const initValues = {
  type: "valet",
  pricingModal: "customByHours",
  exceedingPricingType: "hourly",
  pricingType: "free",
  //   payment: "",
};

interface IProps {
  open: boolean;
  onClose: any;
  locationId?: string;
  serviceDataToEdit?: any;
  serviceDataToEditIndex?: number;
  setServices: Function;
  services: ServiceFormInputs[];
}

const AddEditServicePopup: React.FC<IProps> = (props) => {
  const {
    open,
    onClose,
    locationId,
    serviceDataToEdit,
    serviceDataToEditIndex,
    setServices,
    services,
  } = props;
  const mode = !serviceDataToEdit ? "add" : "edit";
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  const methods = useForm<ServiceFormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      type: "valet",
      pricingModal: "customByHours",
      exceedingPricingType: "hourly",
      pricingType: "free",
      //   payment: "",
    },
  });
  const {
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
    trigger,
    getValues,
  } = methods;

  const handleClose = () => {
    onClose();
  };

  const onSubmitService = async () => {
    const valid = await trigger(
      [
        "type",
        "name",
        "payment",
        "price",
        "pricingType",
        "exceedingPricingType",
        "gracePeriod",
        "priceOfExceedingHours",
        "pricingModal",
        "startingHours",
        "ticketViolationPrice",
      ],
      { shouldFocus: true },
    );
    if (!valid) return;
    const data = getValues();
    // validate to have only one self-parking service
    const isSelfServiceAlreadyExists = !!services.find(
      (service, index) =>
        service.type === "selfParking" &&
        !(mode === "edit" && serviceDataToEditIndex === index),
    );
    if (
      locationId !==
        MULTI_SELF_PARKING_LOCATION_IDS[process.env.REACT_APP_ENV ?? ""] &&
      isSelfServiceAlreadyExists &&
      data.type === "selfParking"
    ) {
      setNotifications((prevNotifications) => [
        ...prevNotifications,
        {
          type: "error",
          message:
            "Sorry! This location already has a self-parking service, you can't create another one",
        },
      ]);
      return;
    }
    // validate naming; to have no repeated names for the same type (valet or self-parking)
    const selfServicesNames = services.map((service, index) => {
      if (
        (mode === "edit" && index === serviceDataToEditIndex) ||
        service.type === "valet"
      ) {
        return;
      }
      return service.name.trim().toLocaleLowerCase();
    });
    const valetServicesNames = services.map((service, index) => {
      if (
        (mode === "edit" && index === serviceDataToEditIndex) ||
        service.type === "selfParking"
      ) {
        return;
      }
      return service.name.trim().toLocaleLowerCase();
    });
    if (
      selfServicesNames.includes(data.name.trim().toLocaleLowerCase()) &&
      data.type === "selfParking"
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            "You have a self-parking service with the same name already!",
        },
      ]);
      return;
    }
    if (
      valetServicesNames.includes(data.name.trim().toLocaleLowerCase()) &&
      data.type === "valet"
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "You have a valet service with the same name already!",
        },
      ]);
      return;
    }

    if (mode === "add") {
      setServices((prev: any) => [...prev, data]);
    } else {
      setServices((prev: any) => {
        const dataUpdated = [...prev];
        dataUpdated[serviceDataToEditIndex as number] = data;
        return dataUpdated;
      });
    }
    handleClose();
  };

  useEffect(() => {
    if (watch("type") === "valet") {
      setValue("payment", "flexible");
    } else if (watch("type") === "golfCar") {
      setValue("payment", "flexible");
      if (
        //types do not belong to golf cart
        watch("pricingType") === "hourly" ||
        watch("pricingType") === "daily"
      ) {
        setValue("pricingType", "fixed");
      }
    } else if (
      watch("type") === "partialCarWash" ||
      watch("type") === "fullCarWash"
    ) {
      setValue("payment", "flexible");
      setValue("pricingType", "fixed");
    } else {
      setValue("payment", "exit");
      if (watch("pricingType") === "custom" && watch("payment") === "entry") {
        setValue("pricingType", "free");
      }
    }
    if (watch("pricingType") === "daily") {
      setValue("pricingType", "free");
    }
    if (watch("exceedingPricingType") === "twelveHour") {
      setValue("exceedingPricingType", "hourly");
    }
  }, [watch("type")]);

  useEffect(() => {
    if (watch("payment") === "entry" && watch("pricingType") === "custom") {
      setValue("pricingType", "free");
    }
  }, [watch("payment")]);

  useEffect(() => {
    if (serviceDataToEdit) {
      reset({ ...serviceDataToEdit });
    }
  }, [serviceDataToEdit]);

  return (
    <Dialog
      fullWidth={true}
      open={open}
      onClose={handleClose}
      component={"div"}
      data-testid="location-form-service-dialog"
      PaperProps={{
        sx: {
          maxWidth: "1100px",
        },
      }}
    >
      <DialogTitle
        sx={{ p: { xs: "22px 10px 25px 10px", md: "22px 32px 25px 40px" } }}
      >
        <Box display={"flex"} justifyContent={"flex-end"}>
          <IconButton size="small" sx={{ ml: "auto" }} onClick={handleClose}>
            <img src={ClosIcon} alt="" />
          </IconButton>
        </Box>
        <Typography fontSize={"22px"} fontWeight={600} lineHeight={"22px"}>
          Service Configuration
        </Typography>
      </DialogTitle>
      <form>
        <Box px={{ xs: "10px", md: "40px" }}>
          {/* Service Type */}
          <Typography fontSize={"18px"} fontWeight={500}>
            Service Type
          </Typography>
          <FormControl
            fullWidth
            size="small"
            error={!!errors.type}
            sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
          >
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <StyledRadioGroup
                  {...field}
                  key={field.value}
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  data-testid="location-form-service-type"
                >
                  <FormControlLabel
                    value="valet"
                    control={<Radio data-testid="service-type-valet" />}
                    label="Valet Service"
                  />
                  <FormControlLabel
                    value="selfParking"
                    control={<Radio data-testid="service-type-selfParking" />}
                    label="Self Parking"
                  />
                  <FormControlLabel
                    value="golfCar"
                    control={<Radio data-testid="service-type-selfParking" />}
                    label="Golf Cart"
                  />
                  <FormControlLabel
                    value="partialCarWash"
                    control={<Radio data-testid="service-type-selfParking" />}
                    label="Partial Car Wash"
                  />
                  <FormControlLabel
                    value="fullCarWash"
                    control={<Radio data-testid="service-type-selfParking" />}
                    label="Complete Car Wash"
                  />
                </StyledRadioGroup>
              )}
            />
          </FormControl>
          <Stack direction={"row"} gap={2} mt={2}>
            <Box>
              {/* Service Name */}
              <Typography mb={2} fontSize={"18px"} fontWeight={500}>
                Service Name
              </Typography>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    {...field}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    fullWidth
                    data-testid="location-form-service-name"
                  />
                )}
              />
            </Box>
          </Stack>

          {/* payment */}
          <Typography fontSize={"18px"} fontWeight={500} mt={"32px"}>
            Payment
          </Typography>
          <FormControl
            fullWidth
            size="small"
            error={!!errors.payment}
            sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
          >
            <Controller
              name="payment"
              control={control}
              render={({ field }) => (
                <StyledRadioGroup
                  {...field}
                  key={field.value}
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  data-testid="location-form-service-payment"
                >
                  <FormControlLabel
                    value="entry"
                    control={<Radio data-testid="payment-entry" />}
                    label="At Entry"
                    disabled={
                      watch("type") === "valet" ||
                      watch("type") === "golfCar" ||
                      watch("type") === "partialCarWash" ||
                      watch("type") === "fullCarWash"
                    }
                  />
                  <FormControlLabel
                    value="exit"
                    control={<Radio data-testid="payment-exit" />}
                    label="At Exit"
                    disabled={
                      watch("type") === "valet" ||
                      watch("type") === "golfCar" ||
                      watch("type") === "partialCarWash" ||
                      watch("type") === "fullCarWash"
                    }
                  />
                  <FormControlLabel
                    value="flexible"
                    control={<Radio data-testid="payment-flexible" />}
                    label="Flexible (At Entry or At Exit)"
                    disabled={watch("type") === "selfParking"}
                  />
                </StyledRadioGroup>
              )}
            />
          </FormControl>
          {/* pricing */}
          <Typography fontSize={"18px"} fontWeight={500} mt={"32px"}>
            Pricing
          </Typography>
          <FormControl
            fullWidth
            size="small"
            error={!!errors.pricingType}
            sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
          >
            <Controller
              name="pricingType"
              control={control}
              render={({ field }) => (
                <StyledRadioGroup
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setValue("price", null as any);
                    setValue("gracePeriod", null as any);
                    setValue("priceOfExceedingHours", null as any);
                    setValue("ticketViolationPrice", 0);
                    setValue("startingHours", null as any);
                  }}
                  key={field.value}
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  data-testid="location-form-service-pricing-type"
                >
                  {watch("type") !== "partialCarWash" &&
                  watch("type") !== "fullCarWash" ? (
                    <FormControlLabel
                      value="free"
                      control={<Radio data-testid="pricing-free" />}
                      label="Free"
                    />
                  ) : null}

                  <FormControlLabel
                    value="fixed"
                    control={<Radio data-testid="pricing-fixed" />}
                    label="Fixed price"
                  />

                  {watch("type") !== "golfCar" &&
                  watch("type") !== "partialCarWash" &&
                  watch("type") !== "fullCarWash" ? (
                    <FormControlLabel
                      value="hourly"
                      control={<Radio data-testid="pricing-hourly" />}
                      label="Hourly"
                    />
                  ) : null}

                  {watch("type") === "valet" ? ( // only for valet
                    <FormControlLabel
                      value="daily"
                      control={<Radio data-testid="pricing-daily" />}
                      label="Daily (24 hours)"
                    />
                  ) : null}

                  {watch("type") !== "partialCarWash" &&
                  watch("type") !== "fullCarWash" ? (
                    <FormControlLabel
                      value="custom"
                      control={<Radio data-testid="pricing-custom" />}
                      label="Custom price"
                      disabled={
                        watch("type") === "selfParking" &&
                        watch("payment") === "entry"
                      }
                    />
                  ) : null}
                </StyledRadioGroup>
              )}
            />
          </FormControl>
          {/* fixed or hourly pricing */}
          {(watch("pricingType") === "fixed" ||
            watch("pricingType") === "hourly" ||
            watch("pricingType") === "daily") && (
            <Stack>
              {watch("payment") === "exit" && (
                <Controller
                  name="gracePeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      error={!!errors.gracePeriod}
                      helperText={errors.gracePeriod?.message}
                      fullWidth
                      sx={{ maxWidth: "212px", marginTop: "14px" }}
                      label="Grace period in mins"
                      data-testid="location-form-grace-period"
                    />
                  )}
                />
              )}
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <TextField
                    type="number"
                    size="small"
                    {...field}
                    value={field.value || ""}
                    error={!!errors.price}
                    helperText={errors.price?.message}
                    fullWidth
                    sx={{ maxWidth: "212px", marginTop: "14px" }}
                    label="Price"
                    data-testid="location-form-price"
                  />
                )}
              />
            </Stack>
          )}
          {/* custom pricing */}
          {watch("pricingType") === "custom" && (
            <Box>
              {watch("payment") === "exit" && (
                <Controller
                  name="gracePeriod"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      error={!!errors.gracePeriod}
                      helperText={errors.gracePeriod?.message}
                      fullWidth
                      sx={{ maxWidth: "212px", marginTop: "14px" }}
                      label="Grace period in mins"
                      data-testid="location-form-grace-period"
                    />
                  )}
                />
              )}
              <Stack
                direction={"row"}
                gap={1}
                alignItems={"flex-start"}
                mt={2}
                flexWrap={"wrap"}
              >
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.pricingModal}
                  sx={{ maxWidth: "172px" }}
                >
                  <InputLabel id="demo-simple-select-label">
                    Pricing Modal
                  </InputLabel>
                  <Controller
                    name="pricingModal"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        key={field.value}
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Pricing Modal"
                        size="small"
                        data-testid="pricing-modal-select"
                      >
                        <MenuItem value={"customByHours"}>
                          Custom By Hours
                        </MenuItem>
                      </Select>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.pricingModal?.message}
                  </FormHelperText>
                </FormControl>
                <Controller
                  name="startingHours"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      error={!!errors.startingHours}
                      helperText={errors.startingHours?.message}
                      fullWidth
                      sx={{ maxWidth: "172px" }}
                      label="Starting Hours"
                      data-testid="custom-starting-hours"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      value={field.value || ""}
                      error={!!errors.price}
                      helperText={errors.price?.message}
                      fullWidth
                      sx={{ maxWidth: "172px" }}
                      label="Price"
                      data-testid="location-form-price"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
                <Controller
                  name="priceOfExceedingHours"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      error={!!errors.priceOfExceedingHours}
                      helperText={errors.priceOfExceedingHours?.message}
                      fullWidth
                      sx={{ maxWidth: "172px" }}
                      label="Price For Exceeding Hours"
                      data-testid="custom-exceeding-price"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />
                <Box height={"100%"} ml={2}>
                  <Typography fontSize={"12px"} fontWeight={500}>
                    Exceeding Price Type
                  </Typography>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.exceedingPricingType}
                    sx={{
                      width: "fit-content",
                      ml: { xs: "auto", sm: "unset" },
                      mt: "4px",
                    }}
                  >
                    <Controller
                      name="exceedingPricingType"
                      control={control}
                      render={({ field }) => (
                        <StyledRadioGroup
                          {...field}
                          key={field.value}
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          data-testid="exceeding-pricing-type"
                        >
                          <FormControlLabel
                            value="hourly"
                            control={
                              <Radio
                                size="small"
                                sx={{ padding: "0 7px" }}
                                data-testid="exceeding-type-hourly"
                              />
                            }
                            label="Per Hour"
                          />
                          <FormControlLabel
                            value="fixed"
                            control={
                              <Radio
                                size="small"
                                sx={{ padding: "0 7px" }}
                                data-testid="exceeding-type-fixed"
                              />
                            }
                            label="All Hours"
                          />
                          {watch("type") === "valet" && (
                            <FormControlLabel
                              value="twelveHour"
                              control={
                                <Radio
                                  size="small"
                                  sx={{ padding: "0 7px" }}
                                  data-testid="exceeding-type-fixed"
                                />
                              }
                              label="12-hours"
                            />
                          )}
                        </StyledRadioGroup>
                      )}
                    />
                  </FormControl>
                </Box>
              </Stack>
              {watch("payment") === "exit" && (
                <Controller
                  name="ticketViolationPrice"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      size="small"
                      {...field}
                      error={!!errors.ticketViolationPrice}
                      helperText={errors.ticketViolationPrice?.message}
                      fullWidth
                      sx={{ maxWidth: "212px", marginTop: "14px" }}
                      label="Ticket violation price"
                      data-testid="ticket-violation-price"
                    />
                  )}
                />
              )}
            </Box>
          )}
        </Box>

        <DialogActions
          sx={{
            padding: { xs: "30px 20px 50px 20px", md: "30px 50px 50px 40px" },
          }}
        >
          <Button
            sx={{ minWidth: "208px" }}
            variant="contained"
            onClick={onSubmitService}
            data-testid="location-form-service-submit"
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditServicePopup;
